<template>
    <v-card width="100%" outlined elevation="0">
        <v-card-actions class="ma-0 pa-0">
            <v-overlay v-if="loading || exportConfig || setToMaster">
              <v-progress-circular indeterminate color="primary"/>
            </v-overlay>
            <v-col class="ma-1 pa-1">
              <v-btn :disabled="selected.length <= 0 || !ifCanDeleteConfig()"
                     color="red"
                     class="mb-2"
                     @click="onPressDeleteSelected">
                Delete selected
              </v-btn>
              <v-data-table ref="table"
                            group-by="group"
                            style="width: 100%;"
                            fixed-header
                            :items="configs"
                            :item-value="item => item.uid"
                            :headers="headers"
                            v-model="selected"
                            hide-default-footer
                            disable-pagination
                            show-select>

                <template v-slot:item.group="{ item }">
                  <v-card-text class="orange--text ma-0 pa-0">{{ item.group }}</v-card-text>
                </template>

                <template v-slot:item.tag="{ item }">
                  <v-card-text class="green--text ma-0 pa-0">{{ getTagText(item.tag) }}</v-card-text>
                </template>

                <template v-slot:item.owner="{ item }">
                  <v-card-text class="blue--text ma-0 pa-0">{{ getUserName(item) }}</v-card-text>
                </template>

                <template v-slot:item.created="{ item }">
                  <v-card-text class="ma-0 pa-0">{{ item.created | formatDate }}</v-card-text>
                </template>

                <template v-slot:item.aud="{ item }">
                  <v-btn :disabled="!ifCanEditAudience()"
                         outlined
                         @click="onPressEditAudience(item)"
                         small class="orange--text pa-2"
                  >{{ (item.aud == null ? "" : item.aud).toUpperCase()}}
                  </v-btn>
                </template>

                <template v-slot:item.application="{ item }">
                  <v-btn :disabled="!ifCanEditVersion()"
                         outlined
                         color="green"
                         class="pa-2"
                         @click="onPressEditVersion(item)">
                    {{ item.minver || '-'}} - {{ item.maxver || '-'}}
                  </v-btn>
                </template>

                <template v-slot:item.master="{ item }">
                  <v-tooltip bottom max-width="50%">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn :loading="setToMaster == item.uid"
                             icon
                             :color="item.master ? 'green' : 'grey lighten-2'"
                             v-bind="attrs"
                             v-on="on"
                             @click="onPressSetMaster(item)">
                        <v-icon>{{item.master ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline'}}</v-icon>
                      </v-btn>
                    </template>
                    <span>Set Master</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom max-width="50%">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn :loading="loading == item.uid"
                             icon
                             color="orange"
                             v-bind="attrs"
                             v-on="on"
                             class="ma-0 pa-0"
                             @click="onPressInfo(item)">
                        <v-icon size="22">mdi-information</v-icon>
                      </v-btn>
                    </template>
                    <span>{{item.info != undefined && item.info.length != 0 ? item.info : "no info"}}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn :disabled="!ifCanExportConfig()"
                             :loading="exportConfig == item.uid"
                             icon
                             color="teal lighten-1"
                             v-bind="attrs"
                             v-on="on"
                             class="ma-0 pa-0"
                             @click="onPressExport(item)">
                        <v-icon size="20">mdi-content-copy</v-icon>
                      </v-btn>
                    </template>
                    <span>Export to my configs</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn :loading="loading == item.uid"
                             icon
                             color="red"
                             v-bind="attrs"
                             v-on="on"
                             class="ma-0 pa-0"
                             :disabled="!ifCanDeleteConfig() || item.master"
                             @click="onPressDelete(item)">
                        <v-icon size="22">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete from storage</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn :disabled="!ifCanDeployConfig()"
                             icon
                             color="blue"
                             v-bind="attrs"
                             v-on="on"
                             class="ma-0 pa-0"
                             @click="onPressAddToDeploy(item.name)">
                        <v-icon size="22">mdi-folder-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Add to deploy</span>
                  </v-tooltip>
                </template>

              </v-data-table>
            </v-col>
        </v-card-actions>
    </v-card>
</template>

<script>

import {ConfigBackendUtils} from "@/modules/mconfigs/database/ConfigBackendUtils";
import {PermissionsStoreUtils} from "@/modules/mpermissions/database/PermissionsStoreUtils";
import {ConfigModel} from "@/modules/mconfigs/models/ConfigModel";
import {ConfigStoreUtils} from "@/modules/mconfigs/database/ConfigStoreUtils";
import {SlackLogManager} from "@/common/SlackLogManager";
import {ReleaseExportUtils} from "@/modules/mconfigs/utils/ReleaseExportUtils";

export default {
    name: "c-release-table",
    data: () => ({
        headers: [
            {text: 'Group', sortable: true, value: 'group', class: "white blue--text", color: "red"},
            {text: 'Tag', sortable: true, value: 'tag', class: "white blue--text", color: "red"},
            {text: 'Owner', sortable: true, value: 'owner', class: "white blue--text", color: "red"},
            {text: 'Created', sortable: true, value: 'created', class: "white blue--text", color: "red"},
            {text: 'File Name', sortable: true, value: 'name', class: "white blue--text"},
            {text: 'Checksum', sortable: true, value: 'checksum', class: "white blue--text"},
            {text: 'Audience', sortable: true, value: 'aud', class: "white blue--text", color: "red"},
            {text: 'App Version', sortable: true, value: 'application', class: "white blue--text"},
            {text: 'Master', sortable: true, value: 'master', class: "white blue--text", width: '90px'},
            {text: 'Actions', sortable: false, value: 'actions', width: '180px', class: "white blue--text"}
        ],
        loading: null,
        setToMaster: null,
        exportConfig: null,
        selected: [],
    }),
    mounted() {
        Object.keys(this.$refs.table.openCache).forEach(g => this.$refs.table.openCache[g] = false)
    },
    methods: {
        getMyUserUid(){
            return PermissionsStoreUtils.getMyUserUid();
        },
        ifCanEditAudience(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_RE_EDIT_AUDIENCE.id)
        },
        ifCanEditVersion(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_RE_EDIT_AUDIENCE.id)
        },
        ifCanDeleteConfig(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_RE_DELETE.id)
        },
        ifCanSetInfo(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_RE_SET_INFO.id)
        },
        ifCanSetMaster(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_RE_SET_MASTER.id)
        },
        ifCanExportConfig(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.CONFIG_TAB.subpermissions.CONFIG_TAB_RE_EXPORT.id)
        },
        onPressSetMaster(config){
            if (config.master == true){return}
            if (!this.ifCanSetMaster()) {
                this.$notify({title: 'No permissions', text: "", type: "error"});
                return
            }
            this.setToMaster = config.uid
            this.$confirm("Are you sure?").then(() => {
                ConfigBackendUtils.setConfigMaster([{"name":config.name, "master": !config.master}])
                .then(() => {
                    this.setToMaster = null
                    this.$notify({title: 'Master successfully updated!', text: "", type: "success"});
                    this.exportToMaster(config)
                })
                .catch((error) => {
                    this.setToMaster = null
                    this.$notify({title: 'Master update failed!', text: error, type: "error"});
                })
            }).catch(() => {this.setToMaster = null})
            //console.log(config.master)
        },
        onPressExport(config){
            this.exportConfig = config.uid

            const doExport = (configData, sharedConfigUid) => {
                let configCopy = new ConfigModel(config.group +"/"+ config.tag +"/"+ config.name, config.mapId)
                configCopy.info = config.info
                configCopy.meta = config.meta
                configCopy.tag = config.tag
                if (sharedConfigUid) {
                    configCopy.sharedConfig = sharedConfigUid
                }
                ConfigStoreUtils.pushConfig(configCopy, configData).then(() => {
                    this.exportConfig = null
                    this.$notify({title: 'Config successfully exported!', text: "", type: "success"});
                }).catch((error) => {
                    this.exportConfig = null
                    this.$notify({title: 'Export config failed!', text: error, type: "error"});
                })
            }

            const fallbackExport = () => {
                ConfigBackendUtils.getConfigContent(config.name)
                    .then((data) => { doExport(data[0], null) })
                    .catch((error) => {
                        this.exportConfig = null
                        this.$notify({title: 'Export config failed!', text: error, type: "error"});
                    })
            }

            ConfigStoreUtils.getDeployedSourceByName(config.name)
                .then((source) => {
                    let resolved = ReleaseExportUtils.resolveConfigExportSource(
                        source,
                        ConfigStoreUtils.getSharedConfigs()
                    );
                    if (resolved) {
                        doExport(resolved.data, resolved.sharedConfigUid)
                    } else {
                        fallbackExport()
                    }
                })
                .catch(() => { fallbackExport() })
        },

        exportToMaster(config){
            const doExportToMaster = (configData, sharedConfigUid) => {
                let configCopy = new ConfigModel(config.group +"/"+ config.tag, config.mapId)
                configCopy.info = config.info
                configCopy.meta = config.meta
                configCopy.tag = config.tag
                if (sharedConfigUid) {
                    configCopy.sharedConfig = sharedConfigUid
                }
                ConfigStoreUtils.pushMasterConfig(configCopy, configData)
            }

            const fallbackExportToMaster = () => {
                ConfigBackendUtils.getConfigContent(config.name)
                    .then((data) => { doExportToMaster(data[0], null) })
                    .catch((error) => {
                        this.setToMaster = null
                        this.$notify({title: 'Export config failed!', text: error, type: "error"});
                    })
            }

            ConfigStoreUtils.getDeployedSourceByName(config.name)
                .then((source) => {
                    let resolved = ReleaseExportUtils.resolveMasterExportSource(source);
                    if (resolved) {
                        doExportToMaster(resolved.data, resolved.sharedConfigUid)
                    } else {
                        fallbackExportToMaster()
                    }
                })
                .catch(() => { fallbackExportToMaster() })
        },

        onPressInfo(config){
            if (!this.ifCanSetInfo()){
                this.$notify({title: 'No permissions', text: "", type: "error"});
                return
            }
            this.$emit('show-configs-popup-edit-info', config)
        },
        getTagText(tag){
            let text = tag || ""
            if (text.length > 25){
                text = text.substring(0,30) + "..."
            }
            return text// + text.length
        },
        ifCanDeployConfig(){
            return PermissionsStoreUtils.getPermission(this.getMyUserUid(), PermissionsStoreUtils.DEPLOY_TAB.subpermissions.DEPLOY_TAB_ADD_CONFIG_TO_DEPLOY.id)
        },
        getUserName(user){
            return PermissionsStoreUtils.getUserName(user.owner)
        },
        onPressEditAudience(config){
            this.$emit('show-c-edit-audience-popup', config)
        },
        onPressAddToDeploy(name){
            this.$store.commit('deploy-store/addPendingForDeploy', name)
            this.$notify({title: 'Added: ' + name, text: "", type: "success"});
        },
        onPressEditVersion(config){
            this.$emit('show-popup-edit-version', config)
        },
        onPressDelete(config) {
            this.$confirm("Are you sure?").then(() => {
                this.loading = config.uid
                ConfigBackendUtils.deleteConfig(config)
                .then(() => {
                    this.loading = null
                    this.$notify({title: 'Config successfully deleted!', text: "", type: "success"});

                    let deployData = {
                        'icon_emoji': ':hammer:',
                        'text': "<@"+PermissionsStoreUtils.getMyName()+"> delete config from `RELEASE`",
                        'attachments': [{
                            'color': '#ee4040',
                            'fields': [
                                {"title": "Environment", 'value': SlackLogManager.getEnv(), 'short': true},
                                {"title": "Name", 'value': config.tag, 'short': true},
                                {"title": "File Name", 'value': config.name, 'short': true},
                                //{"title": "Copy target", 'value': databaseConfig.tag, 'short': true},
                                {"title": "Group", 'value': config.group, 'short': true},
                                //{"title": "Info", 'value': config.info, 'short': true},
                            ]
                        }]
                    }
                    SlackLogManager.log(deployData)

                })
                .catch((error) => {
                    this.loading = null
                    this.$notify({title: 'Config delete failed!', text: error, type: "error"});
                })
            })
        },
        onPressDeleteSelected() {
            this.$confirm("Are you sure to delete " + this.selected.length + " configs?").then(() => {
                this.loading = true
                ConfigBackendUtils.deleteConfigs(this.selected)
                .then(() => {
                    this.loading = null
                    this.$notify({title: 'Configs successfully deleted!', text: "", type: "success"});
                    this.selected = [];
                })
                .catch((error) => {
                    this.loading = null
                    this.$notify({title: 'Configs delete failed!', text: error, type: "error"});
                })
            })
        }
    },
    computed: {
        configs() {
            return ConfigBackendUtils.getConfigs()
        }
    }
}
</script>

<style scoped>

</style>
